<template>
	<div class="businesses-select-container" :class="{manual: userStore.manualCode}">
		<div class="businesses-select-header" @click="clickHeader" @focusout="handleFocusOut" tabindex="0">
			<div class="business-item">
				<div class="business-item-logo">
					<img :src="currentBusinessLogo" alt="logo">
				</div>
				<div class="business-item-name">
					<div class="p-name">{{ currentBusinessName }}</div>
					<div class="p-user" v-if="unlockUser">{{ unlockUser }}</div>
				</div>
			</div>
			<AppChevronIcon class="header-chevron" :direction="chevronDirection" />
		</div>
		<AppDropdown absolute :collapsed="collapsed" @toggled-dropdown="">
			<div class="businesses-select-body">
				<div class="business-item" v-for="(business, index) in selectableBusinesses" :key="business.business_id" @click="clickBusiness(business)">
					<div class="business-item-logo">
						<img :src="business.logo ? business.logo : 'https://img.qromo.io/img/vue/logo/rgb/sign-color.svg'" alt="logo">
					</div>
					<div class="business-item-name">
						<div class="p-name">{{business.name}}</div>
					</div>
				</div>
				<div class="business-item item-new-business" @click="clickAdd" v-if="!isNative">
					<div class="business-item-logo">
						<div class="logout-icon" v-html="getIcon('iconAdd')"></div>
					</div>
					<div class="business-item-name">
						<div class="p-name">{{ getString('new_business') }}</div>
					</div>
				</div>
				<div class="business-item item-logout" @click="clickLogout">
					<div class="business-item-logo">
						<div class="logout-icon" v-html="getIcon('iconLogout')"></div>
					</div>
					<div class="business-item-name">
						<div class="p-name">{{ getString('logout') }}</div>
					</div>
				</div>
			</div>
		</AppDropdown>
	</div>
</template>

<script>
import AppChevronIcon from "@/shared/components/AppChevronIcon.vue";
import AppDropdown from "@/shared/components/AppDropdown.vue";

import {useUserStore} from "@/console-new/stores";
import {useLanguageStore, useSvgStore} from "@/shared/stores";
import {isNative} from "@/shared/helpers/webapp";

export default {
	setup() {
		let userStore = useUserStore();
		let languageStore = useLanguageStore();
		const svgStore = useSvgStore();
		return {getIcon: svgStore.get, userStore, getString: languageStore.getString };
	},
	name: "BusinessesSelect",
	components: {AppDropdown, AppChevronIcon},
	data() {
		return {
			collapsed: true,
		}
	},
	computed: {
		unlockUser() {
			if(this.userStore.requirePin && !!this.userStore.unlockUserId && this.userStore.currBusiness && this.userStore.businessUsers) {
				let user = this.userStore.businessUsers[this.userStore.unlockUserId];
				if (user && user.name) {
					return user.name;
				}
			}
			if(this.userStore.user) {
				return this.userStore.user.name;
			}
			return null;
		},
		isNative() {
			return isNative();
		},
		currentBusinessLogo() {
			let cb = this.userStore.currBusiness;
			return cb && cb.logo ? cb.logo : 'https://img.qromo.io/img/vue/logo/rgb/sign-color.svg';
		},
		currentBusinessName() {
			let cb = this.userStore.currBusiness;
			return cb ? cb.name : this.getString("select_business");
		},
		selectableBusinesses() {
			if(!this.userStore.businesses) {
				return [];
			}
			let validKeys = Object.keys(this.userStore.businesses).filter(businessId => {
				return businessId !== this.userStore.currBusinessId;
			});
			return validKeys.map(business => {
				return this.userStore.businesses[business];
			});
		},
		chevronDirection() {
			return this.collapsed ? "down" : "up";
		}
	},
	methods: {
		clickHeader() {
			this.collapsed = !this.collapsed;
		},
		handleFocusOut() {
			if(!this.collapsed) {
				setTimeout(() => {
					this.collapsed = true;
				}, 100);
			}
		},
		clickBusiness(business) {
			this.userStore.changeBusiness(business.business_id);
		},
		clickLogout() {
			this.userStore.logout();
		},
		clickAdd() {
			this.$router.push("/add-business")
		}
	}
}
</script>

<style lang="scss" scoped>

.businesses-select-container {
	height: 60px;
	width: 100%;
	max-width: 250px;
	background-color: var(--color-bg);
	z-index: 195;

	&.manual {
		background-color: var(--color-manual-3);
	}

	.businesses-select-header {
		width: 100%;
		height: 100%;
		padding-left: 10px;
		padding-right: 10px;
		display: flex;
		align-items: center;
		justify-content: space-between;
		cursor: pointer;
		pointer-events: all;
		outline: none;
		* {
			pointer-events: none;
			@include noSelect;
		}

		.header-chevron {
			margin-left: 5px;
			flex-shrink: 0;
		}

	}

	.businesses-select-body {
		@include hideScrollbar;
		max-height: calc(100vh - 120px);
		overflow-y: auto;
		background-color: var(--color-bg-primary-2);
		border-radius: 0 0 13px 13px;

		.business-item {
			padding: 6px 12px;
			transition: background-color 0.2s $transition;

			&:hover {
				background-color: #25539E4D;
			}

			&.item-logout {
				background-color: rgba(255, 82, 83, 0.2);
				&:hover {
					background-color: rgba(255, 82, 83, 0.3);
				}
			}

			&.item-new-business {
				background-color: rgba(7, 82, 163, 0.12);
				&:hover {
					background-color: rgba(7, 82, 163, 0.2);
				}
			}
		}

	}

	.business-item {
		width: 100%;
		height: 100%;
		display: flex;
		align-items: center;
		justify-content: flex-start;
		cursor: pointer;
		pointer-events: all;

		* {
			pointer-events: none;
			@include noSelect;
		}

		$logoWidth: 35px;

		.business-item-logo {
			width: $logoWidth;
			height: 35px;
			padding: 3px;
			display: flex;
			align-items: center;
			justify-content: center;
			flex-shrink: 0;

			img {
				width: 100%;
				height: 100%;
				border-radius: 10px;
				object-fit: contain;
			}

			.logout-icon {
				width: 15px;
				max-height: 20px;
			}

		}

		.business-item-name {
			display: flex;
			align-items: flex-start;
			justify-content: center;
			flex-direction: column;
			margin-left: 10px;
			flex-grow: 1;
			overflow: hidden;

			.p-name {
				width: 100%;
				font-size: 14px;
				@include truncateString();
			}

			.p-user {
				width: 100%;
				font-size: 12px;
				color: #9B9B9B;
				@include truncateString();
			}
		}
	}

	:deep(.dropdown) {
		border-bottom-right-radius: 13px;
		border-bottom-left-radius: 13px;
		overflow: hidden;

		.dropdown-content {
			padding: 0 !important;
			border-radius: 0 0 13px 13px;
		}
	}

	.businesses-select-header {

		.business-item {
			width: calc(100% - 18px);
		}

	}

	@media (max-width: 768px) {
		height: 50px;

		.businesses-select-body {
			max-height: calc(100vh - 150px);
			overflow-y: auto;
		}
	}
}
</style>